import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  CountryISO,
  NgxIntlTelInputModule,
  SearchCountryField,
} from 'ngx-intl-tel-input';
import { CruiseManagerService } from '../../../services/resources/cruise-manager.service';
import { LanguageMap } from '../../../core/languages';
import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';
import { first } from 'rxjs';
import { CruiseManagerInterface } from '../../../core/models';

@Component({
  selector: 'app-cruise-manager-editor',
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    NgxIntlTelInputModule,
  ],
  standalone: true,
  template: `
    <div class="mx-auto max-w-7xl pt-6 px-4 sm:px-6 lg:px-8">
      <form
        [formGroup]="cruiseManagerForm"
        (ngSubmit)="onSubmit()"
        class="space-y-12"
      >
        <div class="border-b border-gray-900/10 pb-12">
          <h2 class="text-base font-semibold leading-7 text-gray-900">
            Cruise Manager Information
          </h2>
          <p class="mt-1 text-sm leading-6 text-gray-600">
            Enter the details for the guide below.
          </p>

          <div class="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
            <div class="sm:col-span-3">
              <label
                for="forename"
                class="block text-sm font-medium leading-6 text-gray-900"
                >Name</label
              >
              <input
                id="forename"
                type="text"
                formControlName="name"
                class="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-columbus-blue-600 sm:text-sm sm:leading-6"
              />
            </div>

            <div class="sm:col-span-3">
              <label
                for="tel"
                class="mb-2 block text-sm font-medium leading-6 text-gray-900"
                >Phone Number</label
              >
              <ngx-intl-tel-input
                [cssClass]="
                  'block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-columbus-blue-600 sm:text-sm sm:leading-6'
                "
                [preferredCountries]="preferredCountries"
                [enableAutoCountrySelect]="true"
                [enablePlaceholder]="true"
                [searchCountryFlag]="true"
                [searchCountryField]="[
                  SearchCountryField.Iso2,
                  SearchCountryField.Name,
                ]"
                [selectFirstCountry]="false"
                [selectedCountryISO]="countryCode"
                [phoneValidation]="true"
                [separateDialCode]="true"
                formControlName="tel"
              ></ngx-intl-tel-input>
              <div
                *ngIf="
                  cruiseManagerForm.get('tel')?.invalid &&
                  cruiseManagerForm.get('tel')?.touched
                "
                class="text-red-500 text-sm mt-1"
              >
                Valid phone number is required.
              </div>
            </div>
            <div class="sm:col-span-3">
              <label
                for="email"
                class="block text-sm font-medium leading-6 text-gray-900"
                >Email Address</label
              >
              <input
                id="email"
                type="email"
                formControlName="email"
                class="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-columbus-blue-600 sm:text-sm sm:leading-6"
              />
              <div
                *ngIf="
                  cruiseManagerForm.get('email')?.invalid &&
                  cruiseManagerForm.get('email')?.touched
                "
                class="text-red-500 text-sm mt-1"
              >
                Valid email is required.
              </div>
            </div>
          </div>
        </div>

        <div class="mt-6 flex items-center justify-end gap-x-6">
          <button
            type="button"
            (click)="onCancel()"
            class="text-sm font-semibold leading-6 text-gray-900"
          >
            Cancel
          </button>
          <button
            type="submit"
            [disabled]="cruiseManagerForm.invalid"
            class="rounded-md bg-columbus-blue-600 disabled:opacity-25 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-columbus-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-columbus-blue-600"
          >
            Save
          </button>
        </div>
      </form>
    </div>
  `,
  styles: ``,
})
export class CruiseManagerEditorComponent {
  cruiseManagerForm: FormGroup;
  cruiseManagerService = inject(CruiseManagerService);
  fb = inject(FormBuilder);
  route = inject(ActivatedRoute);
  router = inject(Router);

  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  preferredCountries: CountryISO[] = [
    CountryISO.Austria,
    CountryISO.Germany,
    CountryISO.Switzerland,
    CountryISO.Hungary,
    CountryISO.Slovakia,
  ];
  countryCode: CountryISO = CountryISO.Austria;
  PhoneNumberFormat = PhoneNumberFormat;
  id: string | null = null;

  constructor() {
    this.id = this.route.snapshot.paramMap.get('id');

    this.cruiseManagerForm = this.fb.group({
      id: [''],
      name: ['', Validators.required],
      tel: [
        '',
        [
          // this.validatePhoneNumber.bind(this), // Bind properly
        ],
      ],
      email: [''],
    });

    if (this.id) {
      this.cruiseManagerService
        .getCruiseManagerData(this.id)
        .pipe(first())
        .subscribe((cruiseManager: CruiseManagerInterface) => {
          if (cruiseManager) {
            const phoneUtil = PhoneNumberUtil.getInstance();
            const number = phoneUtil.parse(cruiseManager.tel, 'ZZ');
            const countryC = phoneUtil.getRegionCodeForNumber(
              number,
            ) as CountryISO;
            cruiseManager.tel = number.getNationalNumber()?.toString() || '';
            this.countryCode = countryC;
            this.patchForm(cruiseManager);
          }
        });
    }
  }

  patchForm(cruiseManager: CruiseManagerInterface) {
    this.cruiseManagerForm.patchValue({
      id: cruiseManager.id,
      name: cruiseManager.name,
      tel: cruiseManager.tel,
      email: cruiseManager.email,
    });
  }

  onSubmit() {
    if (this.cruiseManagerForm.valid) {
      let cruiseManager: CruiseManagerInterface =
        this.cruiseManagerForm.getRawValue();
      const telValue = this.cruiseManagerForm.get('tel')?.value;
      cruiseManager.tel = telValue?.internationalNumber ?? telValue;

      // Format the phone number
      const phoneUtil = PhoneNumberUtil.getInstance();
      const number = phoneUtil.parse(cruiseManager.tel, 'ZZ');
      cruiseManager.tel = phoneUtil.format(
        number,
        PhoneNumberFormat.INTERNATIONAL,
      );

      if (!this.id) {
        this.cruiseManagerService
          .createNewCruiseManager(cruiseManager)
          .then(() => {
            this.router.navigateByUrl('/cruise-manager');
          });
      } else {
        this.cruiseManagerService
          .updateCruiseManager(cruiseManager)
          .then(() => {
            this.router.navigateByUrl('/cruise-manager');
          });
      }
    } else {
      console.log('Form Invalid');
    }
  }

  onCancel() {
    this.cruiseManagerForm.reset();
    this.router.navigateByUrl('/cruise-manager');
  }

  // Custom validator for phone number
  validatePhoneNumber(control: AbstractControl) {
    const phoneUtil = PhoneNumberUtil.getInstance();
    try {
      const number = phoneUtil.parse(control.value.internationalNumber, 'ZZ'); // 'ZZ' means international format
      return phoneUtil.isValidNumber(number)
        ? null
        : { invalidPhoneNumber: true };
    } catch (e) {
      return { invalidPhoneNumber: true };
    }
  }
}
